import { Link } from 'react-router-dom';

import { parseBlobError } from '../services/blobHandlerError';

export interface BlobHandlerErrorPanelProps {
  /** ``run.error_reason`` from the backend — the message produced by
   *  the FIA + Blob Handler integration when an upload could not be
   *  processed as tabular data. */
  errorReason: string;
}

/**
 * Reviewer-facing panel for v0.4 Stage B Blob Handler rejections.
 *
 * Rendered on `RunDetailPage` when:
 *
 *   - ``run.status === "error"`` AND
 *   - ``isBlobHandlerError(run.error_reason)`` returns true.
 *
 * Replaces the generic red error block with a more helpful explanation:
 * detected file type, why the pipeline rejected it, what the reviewer
 * can do next, and a one-click escape to upload a different file.
 *
 * Parser + predicate live in ``services/blobHandlerError.ts`` so this
 * file exports a single component (Fast Refresh requirement).
 */
export function BlobHandlerErrorPanel({ errorReason }: BlobHandlerErrorPanelProps) {
  const parsed = parseBlobError(errorReason);
  return (
    <section
      aria-labelledby="blob-error-title"
      role="alert"
      className="rounded-md border border-warn/40 bg-warn/5 p-6 space-y-4"
    >
      <div className="flex items-start gap-4">
        <span aria-hidden="true" className="text-3xl select-none">
          📄
        </span>
        <div className="flex-1 space-y-2">
          <h2
            id="blob-error-title"
            className="m-0 text-base font-semibold text-warn"
          >
            {parsed.headline}
          </h2>
          <p className="text-sm text-ink/90 m-0">{parsed.detail}</p>
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-xs uppercase tracking-wide text-muted m-0">
          What to do next
        </h3>
        <ul className="text-sm list-disc ml-5 space-y-1">
          {parsed.suggestions.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ul>
      </div>

      <div className="flex items-center gap-3 pt-2 flex-wrap">
        {/*
         * The upload form is mounted at `/` (App.tsx routes `/` →
         * `UploadPage`). Earlier v0.4 builds pointed this CTA at
         * `/upload` which 404'd into NotFound; the v0.4 Stage F.7
         * follow-up corrected the route. Documented in DESIGN_SPEC
         * known-limitations + the corresponding entry was retired.
         */}
        <Link
          to="/"
          className="inline-flex items-center rounded-sm bg-brand text-white px-3 py-1.5 text-sm font-semibold hover:bg-brand-dark"
        >
          Upload a new file
        </Link>
        <a
          href="/docs/supported-formats"
          target="_blank"
          rel="noreferrer"
          className="text-sm text-brand-dark underline hover:no-underline"
        >
          See supported formats
        </a>
      </div>
    </section>
  );
}
